import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { HugeiconsIcon, Icons } from "@/components/icons"

import { Button } from "@/components/ui/button"
import { HeaderNotifications } from "@/components/header-notifications"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { ThemeToggle } from "@/components/theme-toggle"
import { cn } from "@/lib/utils"
import type { Language } from "@/components/I18nProvider"
import { LANGUAGE_OPTIONS } from "@/lib/i18n/language-options"
import { useIntl } from "react-intl"
// import { useSidebar } from "@/components/ui/sidebar"

const pillClass =
  "border-border bg-card flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-1-light"

const innerPillClass =
  "bg-secondary-background flex items-center gap-2 rounded-xl px-[10px] py-2 h-10 hover:bg-primary-25 text-primary-text"

interface HeaderProps {
  title: string
  language: Language
  onLanguageChange: (lang: Language) => void
  /** Optional warning text (e.g. policy limit). Shown in alert pill when no latest in-app notification. */
  warningMessage?: string | null
  className?: string
}

export function Header({
  title,
  language,
  onLanguageChange,
  warningMessage,
  className,
}: HeaderProps) {
  const intl = useIntl()
  // const { sidebarVisible, toggleSidebarVisibility } = useSidebar()

  return (
    <header
      className={cn(
        "relative z-20 flex shrink-0 items-center justify-between gap-4 p-4 lg:px-6",
        "bg-transparent",
        className
      )}
    >
      {/* Block 1: Page title (+ sidebar trigger on mobile) */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <SidebarTrigger className="shrink-0 max-lg:hidden lg:hidden" />
        <h1
          className="text-primary-text app-text-headline-medium lg:app-text-headline-large truncate!"
          title={title}
        >
          <span className="hidden lg:inline">{title}</span>
          <span className="lg:hidden">
            {intl.formatMessage({ id: "layout.appName" })}
          </span>
        </h1>
      </div>

      {/* Two pills */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Pill 1: Alert (latest notification or warningMessage) + Notifications list + User */}
        <HeaderNotifications warningMessage={warningMessage} />

        {/* Pill 2: Theme + Language select (dropdown) */}
        <div
          className={pillClass}
          aria-label={intl.formatMessage({ id: "layout.languageAndTheme" })}
        >
          {/*
          <Button
            type="button"
            variant="ghost"
            onClick={toggleSidebarVisibility}
            className={innerPillClass}
            aria-label={
              sidebarVisible
                ? intl.formatMessage({ id: "layout.hideSidebar" })
                : intl.formatMessage({ id: "layout.showSidebar" })
            }
            title={
              sidebarVisible
                ? intl.formatMessage({ id: "layout.hideSidebar" })
                : intl.formatMessage({ id: "layout.showSidebar" })
            }
          >
            <HugeiconsIcon
              icon={sidebarVisible ? Icons.PanelLeftOpen : Icons.PanelLeftClose}
              size={20}
            />
          </Button>
          */}

          <ThemeToggle className={innerPillClass} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="bg-secondary-background flex h-10 items-center gap-2 rounded-xl border-none px-2.5 py-2"
              >
                <span className="text-primary-text app-text-title-small">
                  {LANGUAGE_OPTIONS.find((option) => option.value === language)?.code ??
                    language.toUpperCase()}
                </span>
                <HugeiconsIcon
                  icon={Icons.ArrowDown01}
                  size={14}
                  className="text-muted-foreground shrink-0"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="border-border bg-background shadow-1-light w-37.5 overflow-hidden rounded-2xl border p-2"
            >
              {LANGUAGE_OPTIONS.map(({ value, code, label }) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => onLanguageChange(value as Language)}
                  className={cn(
                    "text-primary-text focus:bg-accent-25 flex h-10 cursor-pointer items-center justify-center rounded-xl px-2.5 py-2 outline-none",
                    language === value && "bg-primary-5"
                  )}
                >
                  <span className="app-text-title-small">
                    {code} {label}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
